<template>
  <div v-if="visible" class="merge-group-modal-overlay" @click="handleBackdropClick">
    <div class="merge-group-modal" @click.stop>
      <!-- 模态框头部 -->
      <div class="modal-header">
        <h3>{{ modalTitle }}</h3>
        <button class="close-btn" @click="close">×</button>
      </div>

      <!-- 模态框内容 -->
      <div class="modal-content">
        <!-- 归并组基本信息 -->
        <div class="group-info" v-if="groupDetail">
          <div class="info-row">
            <span class="label">组名称:</span>
            <input 
              v-if="isEditing" 
              v-model="editForm.groupName" 
              class="edit-input"
              placeholder="请输入归并组名称"
            />
            <span v-else class="value">{{ groupDetail.groupName }}</span>
          </div>
          <div class="info-row">
            <span class="label">创建时间:</span>
            <span class="value">{{ formatDate(groupDetail.createdAt) }}</span>
          </div>
          <div class="info-row">
            <span class="label">状态:</span>
            <span class="value status-tag" :class="groupDetail.status">
              {{ getStatusText(groupDetail.status) }}
            </span>
          </div>
          <div class="info-row">
            <span class="label">描述:</span>
            <textarea 
              v-if="isEditing" 
              v-model="editForm.description" 
              class="edit-textarea"
              placeholder="请输入描述信息"
              rows="3"
            ></textarea>
            <span v-else class="value">{{ groupDetail.description || '无' }}</span>
          </div>
        </div>

        <!-- 票据列表 -->
        <div class="documents-section" v-if="documents.length > 0">
          <h4>票据列表 ({{ documents.length }})</h4>
          <div class="documents-grid">
            <div 
              v-for="doc in documents" 
              :key="doc.id"
              class="document-card"
            >
              <div class="card-header">
                <span class="bill-no">{{ doc.billNo }}</span>
                <span class="amount">¥{{ formatAmount(doc.amount) }}</span>
              </div>
              <div class="card-content">
                <div class="info-item">
                  <span class="label">日期:</span>
                  <span class="value">{{ formatDate(doc.billDate) }}</span>
                </div>
                <div class="info-item">
                  <span class="label">开票方:</span>
                  <span class="value">{{ doc.issuer || '-' }}</span>
                </div>
                <div class="info-item">
                  <span class="label">收票方:</span>
                  <span class="value">{{ doc.recipient || '-' }}</span>
                </div>
                <div class="info-item">
                  <span class="label">摘要:</span>
                  <span class="value">{{ doc.summary || '-' }}</span>
                </div>
              </div>
              <div class="card-actions">
                <button 
                  v-if="doc.imageUrl" 
                  class="preview-btn"
                  @click="previewImage(doc)"
                >
                  🖼️ 预览图片
                </button>
                <button 
                  class="detail-btn"
                  @click="viewDocumentDetail(doc)"
                >
                  📄 查看详情
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 银证列表 -->
        <div class="receipts-section" v-if="receipts.length > 0">
          <h4>银证列表 ({{ receipts.length }})</h4>
          <div class="documents-grid">
            <div 
              v-for="receipt in receipts" 
              :key="receipt.id"
              class="document-card"
            >
              <div class="card-header">
                <span class="bill-no">{{ receipt.receiptsNo }}</span>
                <span class="amount">¥{{ formatAmount(receipt.amount) }}</span>
              </div>
              <div class="card-content">
                <div class="info-item">
                  <span class="label">日期:</span>
                  <span class="value">{{ formatDate(receipt.receiptsDate) }}</span>
                </div>
                <div class="info-item">
                  <span class="label">银行:</span>
                  <span class="value">{{ receipt.bankName || '-' }}</span>
                </div>
                <div class="info-item">
                  <span class="label">对方户名:</span>
                  <span class="value">{{ receipt.counterparty || '-' }}</span>
                </div>
                <div class="info-item">
                  <span class="label">摘要:</span>
                  <span class="value">{{ receipt.summary || '-' }}</span>
                </div>
              </div>
              <div class="card-actions">
                <button 
                  v-if="receipt.imageUrl" 
                  class="preview-btn"
                  @click="previewImage(receipt)"
                >
                  🖼️ 预览图片
                </button>
                <button 
                  class="detail-btn"
                  @click="viewReceiptDetail(receipt)"
                >
                  🧾 查看详情
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-if="documents.length === 0 && receipts.length === 0" class="empty-state">
          <p>该归并组暂无关联的票据或银证</p>
        </div>
      </div>

      <!-- 模态框底部 -->
      <div class="modal-footer">
        <div class="footer-left">
          <button v-if="!isEditing" class="btn danger" @click="dissolveGroup">🗑️ 解散归并组</button>
        </div>
        <div class="footer-right">
          <button v-if="!isEditing" class="btn secondary" @click="startEdit">✏️ 编辑</button>
          <button v-if="isEditing" class="btn primary" @click="saveEdit">💾 保存</button>
          <button v-if="isEditing" class="btn secondary" @click="cancelEdit">❌ 取消</button>
          <button class="btn secondary" @click="close">🚪 关闭</button>
        </div>
      </div>
    </div>

    <!-- 图片预览组件 -->
    <ImagePreviewModal
      :visible="imagePreviewVisible"
      :imageUrl="currentImageUrl"
      :imageInfo="currentImageInfo"
      @close="closeImagePreview"
    />
  </div>
</template>

<script>
import { mergeManagementApi } from '../api/merge-management.js'
import ImagePreviewModal from './ImagePreviewModal.vue'

export default {
  name: 'MergeGroupDetailModal',
  components: {
    ImagePreviewModal
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    groupId: {
      type: String,
      default: ''
    },
    groupType: {
      type: String,
      default: 'document' // 'document' 或 'receipt'
    }
  },
  data() {
    return {
      loading: false,
      groupDetail: null,
      documents: [],
      receipts: [],
      isEditing: false,
      editForm: {
        groupName: '',
        description: ''
      },
      
      // 图片预览
      imagePreviewVisible: false,
      currentImageUrl: '',
      currentImageInfo: {}
    }
  },
  computed: {
    modalTitle() {
      if (!this.groupDetail) return '归并组详情'
      const type = this.groupType === 'document' ? '票据' : '银证'
      return `${type}归并组详情 - ${this.groupDetail.groupName}`
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.groupId) {
        this.loadGroupDetail()
      } else {
        this.resetModal()
      }
    },
    groupId(newVal) {
      if (newVal && this.visible) {
        this.loadGroupDetail()
      }
    }
  },
  methods: {
    async loadGroupDetail() {
      if (!this.groupId) return

      this.loading = true
      try {
        let result
        if (this.groupType === 'document') {
          result = await mergeManagementApi.getDocumentGroupDetail(this.groupId)
        } else {
          result = await mergeManagementApi.getReceiptGroupDetail(this.groupId)
        }

        console.log('归并组详情API返回:', result)

        if (result.success) {
          this.groupDetail = result.data
          this.documents = result.data.documents || []
          this.receipts = result.data.receipts || []

          console.log('解析后的数据:', {
            groupDetail: this.groupDetail,
            documents: this.documents,
            receipts: this.receipts
          })

          // 初始化编辑表单
          this.editForm = {
            groupName: this.groupDetail.groupName || '',
            description: this.groupDetail.description || this.groupDetail.groupSummary || ''
          }
        } else {
          console.error('API返回失败:', result)
          this.$Message.error(result.message || '加载归并组详情失败')
        }
      } catch (error) {
        console.error('加载归并组详情失败:', error)
        this.$Message.error('加载归并组详情失败: ' + error.message)
      } finally {
        this.loading = false
      }
    },

    startEdit() {
      this.isEditing = true
    },

    async saveEdit() {
      if (!this.editForm.groupName.trim()) {
        this.$Message.warning('请输入归并组名称')
        return
      }

      try {
        const updateData = {
          groupName: this.editForm.groupName.trim(),
          description: this.editForm.description.trim()
        }

        let result
        if (this.groupType === 'document') {
          result = await mergeManagementApi.updateDocumentGroup(this.groupId, updateData)
        } else {
          result = await mergeManagementApi.updateReceiptGroup(this.groupId, updateData)
        }

        if (result.success) {
          this.$Message.success('更新成功')
          this.isEditing = false
          this.groupDetail.groupName = updateData.groupName
          this.groupDetail.description = updateData.description
          this.$emit('updated')
        } else {
          this.$Message.error(result.message || '更新失败')
        }
      } catch (error) {
        console.error('更新归并组失败:', error)
        this.$Message.error('更新失败')
      }
    },

    cancelEdit() {
      this.isEditing = false
      this.editForm = {
        groupName: this.groupDetail.groupName || '',
        description: this.groupDetail.description || ''
      }
    },

    async dissolveGroup() {
      if (!this.groupDetail) return

      const groupType = this.groupType === 'document' ? '票据' : '银证'
      const itemCount = this.documents.length + this.receipts.length
      const content = `确定要解散${groupType}归并组 "${this.groupDetail.groupName}" 吗？\n\n解散后：\n• 归并组将被删除\n• 组内 ${itemCount} 个${groupType}将恢复为未归并状态\n• 此操作不可撤销`

      this.$Confirm(content, '确认解散归并组').then(async () => {
        try {
          let result
          if (this.groupType === 'document') {
            result = await mergeManagementApi.dissolveDocumentGroup(this.groupId)
          } else {
            result = await mergeManagementApi.dissolveReceiptGroup(this.groupId)
          }

          if (result.success) {
            this.$Message.success(`${groupType}归并组已解散，${itemCount} 个${groupType}已恢复为未归并状态`)
            this.$emit('dissolved') // 通知父组件归并组已解散
            this.close()
          } else {
            this.$Message.error(result.message || '解散失败')
          }
        } catch (error) {
          console.error('解散归并组失败:', error)
          this.$Message.error('解散操作失败')
        }
      }).catch(() => {
        // 用户取消操作
      })
    },

    previewImage(item) {
      // 处理图片URL，确保是完整的URL
      let imageUrl = item.imageUrl || item.filePath
      if (imageUrl && !imageUrl.startsWith('http')) {
        // 如果是相对路径，添加服务器前缀
        imageUrl = `/api/files/view?path=${encodeURIComponent(imageUrl)}`
      }

      this.currentImageUrl = imageUrl
      this.currentImageInfo = {
        fileName: item.billNo || item.receiptsNo || '图片',
        fileSize: item.fileSize || '',
        uploadTime: item.createdAt || ''
      }
      this.imagePreviewVisible = true
    },

    closeImagePreview() {
      this.imagePreviewVisible = false
      this.currentImageUrl = ''
      this.currentImageInfo = {}
    },

    viewDocumentDetail(doc) {
      // 跳转到票据详情页面
      this.$router.push(`/bills/bill-form/${doc.id}`)
    },

    viewReceiptDetail(receipt) {
      // 跳转到银证详情页面
      this.$router.push(`/bills/bank-receipts/form/${receipt.id}`)
    },

    getStatusText(status) {
      const statusMap = {
        'ACTIVE': '活跃',
        'INACTIVE': '非活跃',
        'DISSOLVED': '已解散'
      }
      return statusMap[status] || status
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('zh-CN')
    },

    formatAmount(amount) {
      if (!amount) return '0.00'
      return parseFloat(amount).toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    handleBackdropClick() {
      if (!this.isEditing) {
        this.close()
      }
    },

    close() {
      this.$emit('close')
    },

    resetModal() {
      this.groupDetail = null
      this.documents = []
      this.receipts = []
      this.isEditing = false
      this.editForm = {
        groupName: '',
        description: ''
      }
    }
  }
}
</script>

<style scoped>
.merge-group-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.merge-group-modal {
  background: white;
  border-radius: 12px;
  width: 95%;
  max-width: 1000px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  border: 1px solid #e8e8e8;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 28px;
  border-bottom: 1px solid #e8e8e8;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px 12px 0 0;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: white;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: white;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.modal-content {
  flex: 1;
  overflow-y: auto;
  padding: 28px;
}

.group-info {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 28px;
  border: 1px solid #e8e8e8;
}

.info-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-row .label {
  width: 120px;
  font-weight: 600;
  color: #495057;
  flex-shrink: 0;
  font-size: 14px;
}

.info-row .value {
  flex: 1;
  color: #212529;
  font-size: 14px;
}

.edit-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.edit-textarea {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
}

.status-tag {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-tag.ACTIVE {
  background: #d4edda;
  color: #155724;
}

.status-tag.INACTIVE {
  background: #fff3cd;
  color: #856404;
}

.status-tag.DISSOLVED {
  background: #f8d7da;
  color: #721c24;
}

.documents-section,
.receipts-section {
  margin-bottom: 24px;
}

.documents-section h4,
.receipts-section h4 {
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  color: #495057;
  border-bottom: 3px solid #667eea;
  padding-bottom: 12px;
  position: relative;
}

.documents-section h4::before,
.receipts-section h4::before {
  content: '';
  position: absolute;
  bottom: -3px;
  left: 0;
  width: 60px;
  height: 3px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.documents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 20px;
}

.document-card {
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  padding: 20px;
  background: white;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.document-card:hover {
  border-color: #667eea;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.15);
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f8f9fa;
}

.bill-no {
  font-weight: 700;
  color: #667eea;
  font-size: 15px;
  letter-spacing: 0.5px;
}

.amount {
  font-weight: 700;
  color: #28a745;
  font-size: 16px;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.card-content {
  margin-bottom: 12px;
}

.info-item {
  display: flex;
  margin-bottom: 6px;
  font-size: 13px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-item .label {
  width: 60px;
  color: #666;
  flex-shrink: 0;
}

.info-item .value {
  flex: 1;
  color: #333;
  word-break: break-all;
}

.card-actions {
  display: flex;
  gap: 8px;
}

.preview-btn,
.detail-btn {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.preview-btn:hover {
  border-color: #007bff;
  color: #007bff;
}

.detail-btn:hover {
  border-color: #28a745;
  color: #28a745;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #666;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 28px;
  border-top: 1px solid #e8e8e8;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 0 0 12px 12px;
}

.footer-left {
  display: flex;
  gap: 16px;
}

.footer-right {
  display: flex;
  gap: 16px;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn.primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn.primary:hover {
  background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn.secondary {
  background: #f8f9fa;
  color: #495057;
  border: 1px solid #dee2e6;
}

.btn.secondary:hover {
  background: #e9ecef;
  border-color: #adb5bd;
  transform: translateY(-1px);
}

.btn.danger {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
}

.btn.danger:hover {
  background: linear-gradient(135deg, #c82333 0%, #a71e2a 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .merge-group-modal {
    width: 95%;
    max-height: 95vh;
  }

  .documents-grid {
    grid-template-columns: 1fr;
  }

  .modal-header,
  .modal-content,
  .modal-footer {
    padding: 16px;
  }
}
</style>
